import React from 'react';
import { CircleMarker, MapContainer, TileLayer, Tooltip } from 'react-leaflet';
import { campusCoordinates } from '../../data/discoverVisual';
import type { UniversityProfile } from '../../types/admissions';

/**
 * Where the institutions in the current result set actually are.
 *
 * Markers are placed at each campus's locality. Campuzon does not hold surveyed
 * campus boundaries, so the map is honest about being an orientation aid rather
 * than a measurement — nothing in the product asks a student to rely on a
 * distance read off it.
 */
interface UniversityMapProps {
  universities: UniversityProfile[];
  onSelect: (id: string) => void;
}
export function UniversityMap({
  universities,
  onSelect
}: UniversityMapProps) {
  const points = universities.map(university => ({
    university,
    point: campusCoordinates[university.id]
  })).filter((entry): entry is {
    university: UniversityProfile;
    point: {
      lat: number;
      lng: number;
    };
  } => Boolean(entry.point));
  const missing = universities.length - points.length;
  if (points.length === 0) {
    return <div className="rounded-card border border-line bg-surface px-5 py-10 text-center" data-id="element-1683" data-mp="95v5ejg1rlr6">
        <p className="text-body font-semibold text-ink" data-id="element-1684" data-mp="e57qayncyir4">No locations to place</p>
        <p className="mx-auto mt-1.5 max-w-[46ch] text-body-sm text-ink-muted" data-id="element-1685" data-mp="e7assi5p64cq">
          None of the institutions in this result set has a published location. Switch back to grid
          or list to keep browsing them.
        </p>
      </div>;
  }
  return <div className="space-y-3" data-id="element-1686" data-mp="s3xqwo9dz0k8">
      <div className="h-[28rem] overflow-hidden rounded-card border border-line" data-id="element-1687" data-mp="s2wcqax2pxya">
        <MapContainer center={[points[0].point.lat, points[0].point.lng]} zoom={3} scrollWheelZoom={false} className="h-full w-full" attributionControl={false} data-id="element-1688" data-mp="ww8zsgm3twox">
          <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" data-id="element-1689" data-mp="16rsl6931lczf" />
          {points.map(({
          university,
          point
        }) => <CircleMarker key={university.id} center={[point.lat, point.lng]} radius={9} pathOptions={{
          color: '#ffffff',
          weight: 2,
          fillColor: '#0f766e',
          fillOpacity: 1
        }} eventHandlers={{
          click: () => onSelect(university.id)
        }} data-id="element-1690" data-mp="m41e2uqf38k7">
              <Tooltip direction="top" offset={[0, -8]} data-id="element-1691" data-mp="14uj24m0b1fz8">
                <span className="font-semibold" data-id="element-1692" data-mp="14xewy1guvojg">{university.name}</span>
                <br data-id="element-1693" data-mp="zvpmamxjpcvw" />
                <span data-id="element-1694" data-mp="zybsoj8p9mcj">
                  {university.city}, {university.country}
                </span>
              </Tooltip>
            </CircleMarker>)}
        </MapContainer>
      </div>
      <p className="text-caption text-ink-subtle" data-id="element-1695" data-mp="19sc5766b9tva">
        Markers are placed at each campus's locality, not a surveyed boundary.
        {missing > 0 && ` ${missing} institution${missing === 1 ? '' : 's'} in this result set has no published location and is not shown.`}
      </p>
    </div>;
}